import React, { useState } from 'react';
import {
  AnamneseFormData,
} from '../../../../types/registration';
import BooleanRadio from '../../../../components/common/BooleanRadio/BooleanRadio';
import Button from '../../../../components/common/Button/Button';
import ConfirmModal from '../../../../components/common/ConfirmModal/ConfirmModal';
import BaseFormInput from '../../../../components/common/FormInput/FormInput';
import BaseFormSelect from '../../../../components/common/FormSelect/FormSelect';
import BaseFormTextarea from '../../../../components/common/FormTextarea/FormTextarea';
import { AppFormInput, AppFormTextarea, withRequiredAsterisk } from '../../../../components/common/appForms';
import TextSummaryCard from '../../../../components/common/TextSummaryCard/TextSummaryCard';
import SectionTitle from '../../../../components/common/SectionTitle/SectionTitle';
import Tooltip from '../../../../components/common/Tooltip/Tooltip';
import { AnamnesisAiSummaryService } from '../../../../services/AnamnesisAiSummaryService';
import {
  PUBLIC_ANAMNESE_FIELD_LABELS as L,
  PUBLIC_AVAILABLE_TRAINING_TIME_OPTIONS,
  PUBLIC_MAIN_GOAL_OPTIONS,
  PUBLIC_ONLINE_COURSE_OPTIONS,
  PUBLIC_REFERRAL_SOURCE_OPTIONS,
  PUBLIC_TRAINING_LEVEL_OPTIONS,
  PUBLIC_TRAINING_LOCATION_OPTIONS,
  PUBLIC_WEEKLY_AVAILABILITY_OPTIONS,
  withCurrentSelectOption,
} from '../../../Anamnesis/AnamnesisRegister/publicAnamnese.constants';
import { maskHeight, maskInstagram, maskWeight, formatHeightMeters, formatWeightKg } from '../../../../utils/masks';
import styles from '../StudentRegister.module.css';

interface AnamneseStepProps {
  data: AnamneseFormData;
  errors: Record<string, string>;
  onFieldChange: <K extends keyof AnamneseFormData>(field: K, value: AnamneseFormData[K]) => void;
  readOnly?: boolean;
  useAppInputs?: boolean;
  titleAction?: React.ReactNode;
  studentId?: number | null;
  canGenerateAiSummary?: boolean;
  /** Resumo gerado por IA — somente leitura; o portal não edita este campo. */
  aiSummary?: string | null;
  onAiSummaryGenerated?: (summary: string) => void;
}

const RequiredBooleanLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <label className={styles.label}>
    {withRequiredAsterisk(children, true)}
  </label>
);

const AnamneseStep: React.FC<AnamneseStepProps> = ({
  data,
  errors,
  onFieldChange,
  readOnly = false,
  useAppInputs = false,
  titleAction,
  studentId = null,
  canGenerateAiSummary = false,
  aiSummary = null,
  onAiSummaryGenerated,
}) => {
  const FormInput = useAppInputs ? AppFormInput : BaseFormInput;
  const FormSelect = BaseFormSelect;
  const FormTextarea = useAppInputs ? AppFormTextarea : BaseFormTextarea;
  const [confirmGenerateOpen, setConfirmGenerateOpen] = useState(false);
  const [generatingSummary, setGeneratingSummary] = useState(false);
  const canGenerate = canGenerateAiSummary
    && studentId != null
    && studentId > 0
    && !readOnly;

  const handleText = (
    event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    onFieldChange(event.target.name as keyof AnamneseFormData, event.target.value as any);
  };

  const handleBool = (field: keyof AnamneseFormData, value: boolean) => {
    onFieldChange(field, value as any);
  };

  const handleHeight = (event: React.ChangeEvent<HTMLInputElement>) => {
    onFieldChange('heightCm', maskHeight(event.target.value));
  };

  const handleWeightKg = (event: React.ChangeEvent<HTMLInputElement>) => {
    onFieldChange('weightKg', maskWeight(event.target.value));
  };

  const handleHeightBlur = () => {
    if (readOnly) {
      return;
    }
    onFieldChange('heightCm', formatHeightMeters(data.heightCm));
  };

  const handleWeightBlur = () => {
    if (readOnly) {
      return;
    }
    onFieldChange('weightKg', formatWeightKg(data.weightKg));
  };

  const handleInstagram = (event: React.ChangeEvent<HTMLInputElement>) => {
    onFieldChange('instagram', maskInstagram(event.target.value));
  };

  const handleConfirmGenerate = async () => {
    if (!canGenerate || studentId == null || generatingSummary) {
      return;
    }

    setGeneratingSummary(true);
    try {
      const summary = await AnamnesisAiSummaryService.generate(studentId);
      onAiSummaryGenerated?.(summary);
      setConfirmGenerateOpen(false);
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Não foi possível gerar o resumo da anamnese.');
    } finally {
      setGeneratingSummary(false);
    }
  };

  const generateAccessory = canGenerate ? (
    <Tooltip content="Gerar resumo da anamnese">
      <Button
        type="button"
        variant="ghost"
        size="xs"
        icon="auto_awesome"
        className={styles.aiSummaryButton}
        aria-label="Gerar resumo da anamnese"
        disabled={generatingSummary}
        onClick={() => setConfirmGenerateOpen(true)}
      />
    </Tooltip>
  ) : null;

  return (
    <div className={styles.card}>
      <div>
        <TextSummaryCard
          title="Resumo da anamnese"
          titleAccessory={generateAccessory}
          titleAction={titleAction}
          value={aiSummary}
          emptyText="Não há resumo da anamnese."
        />
      </div>

      <ConfirmModal
        open={confirmGenerateOpen}
        variant="warning"
        icon="auto_awesome"
        title="Gerar resumo?"
        message="Deseja gerar o resumo da anamnese desta aluna? O resumo atual será substituído."
        confirmLabel="Sim"
        cancelLabel="Cancelar"
        confirmIcon="auto_awesome"
        loading={generatingSummary}
        loadingLabel="Gerando..."
        onCancel={() => {
          if (!generatingSummary) {
            setConfirmGenerateOpen(false);
          }
        }}
        onConfirm={() => {
          void handleConfirmGenerate();
        }}
      />

      <div style={{ marginTop: 'var(--spacing-xl)' }}>
        <SectionTitle as="h2" text="Informações de Treino" />
        <div className={styles.formGrid}>
          <FormInput
            label={L.heightCm}
            name="heightCm"
            value={data.heightCm}
            onChange={handleHeight}
            onBlur={handleHeightBlur}
            placeholder="1,75"
            inputMode="decimal"
            maxLength={4}
            error={errors.heightCm}
            readOnly={readOnly}
            required
          />

          <FormInput
            label={L.weightKg}
            name="weightKg"
            value={data.weightKg}
            onChange={handleWeightKg}
            onBlur={handleWeightBlur}
            placeholder="60,45"
            inputMode="decimal"
            maxLength={6}
            error={errors.weightKg}
            readOnly={readOnly}
            required
          />

          <FormInput
            label={L.profession}
            name="profession"
            value={data.profession}
            onChange={handleText}
            error={errors.profession}
            readOnly={readOnly}
          />

          <FormInput
            label={L.instagram}
            name="instagram"
            value={data.instagram}
            onChange={handleInstagram}
            error={errors.instagram}
            readOnly={readOnly}
          />

          <div className={`${styles.formGrid3Col} ${styles.alignedFieldRow}`}>
            <FormSelect
              label={L.weeklyAvailability}
              name="weeklyAvailability"
              value={data.weeklyAvailability}
              onChange={handleText}
              options={withCurrentSelectOption(PUBLIC_WEEKLY_AVAILABILITY_OPTIONS, data.weeklyAvailability)}
              containerClassName={styles.alignedField}
              error={errors.weeklyAvailability}
              readOnly={readOnly}
              required
            />

            <FormSelect
              label={L.availableTrainingTime}
              name="availableTrainingTime"
              value={data.availableTrainingTime}
              onChange={handleText}
              options={withCurrentSelectOption(PUBLIC_AVAILABLE_TRAINING_TIME_OPTIONS, data.availableTrainingTime)}
              containerClassName={styles.alignedField}
              error={errors.availableTrainingTime}
              readOnly={readOnly}
              required
            />

            <FormTextarea
              label={L.previousOnlineConsultancy}
              name="previousOnlineConsultancy"
              value={data.previousOnlineConsultancy}
              onChange={handleText}
              containerClassName={styles.alignedField}
              error={errors.previousOnlineConsultancy}
              readOnly={readOnly}
              required
            />
          </div>

          <FormSelect
            label={L.onlineCourse}
            name="onlineCourse"
            value={data.onlineCourse}
            onChange={handleText}
            options={[...PUBLIC_ONLINE_COURSE_OPTIONS]}
            error={errors.onlineCourse}
            readOnly={readOnly}
            required
          />

          {data.onlineCourse === 'other' && (
            <FormTextarea
              label="Qual curso?"
              name="otherCourse"
              value={data.otherCourse}
              onChange={handleText}
              containerClassName={styles.fieldGroupFull}
              error={errors.otherCourse}
              readOnly={readOnly}
              required
            />
          )}

          <FormTextarea
            label={L.courseFeedback}
            name="courseFeedback"
            value={data.courseFeedback}
            onChange={handleText}
            containerClassName={styles.fieldGroupFull}
            error={errors.courseFeedback}
            readOnly={readOnly}
            required
          />

          <FormSelect
            label={L.trainingLevel}
            name="trainingLevel"
            value={data.trainingLevel}
            onChange={handleText}
            options={PUBLIC_TRAINING_LEVEL_OPTIONS.map(({ value, label }) => ({ value, label }))}
            error={errors.trainingLevel}
            readOnly={readOnly}
            required
          />

          {data.trainingLevel === 'other' && (
            <FormTextarea
              label="Descreva o nível de treinamento"
              name="trainingLevelOther"
              value={data.trainingLevelOther}
              onChange={handleText}
              containerClassName={styles.fieldGroupFull}
              error={errors.trainingLevelOther}
              readOnly={readOnly}
              required
            />
          )}

          <FormSelect
            label={L.trainingLocation}
            name="trainingLocation"
            value={data.trainingLocation}
            onChange={handleText}
            options={[...PUBLIC_TRAINING_LOCATION_OPTIONS]}
            error={errors.trainingLocation}
            readOnly={readOnly}
            required
          />

          {data.trainingLocation === 'full_gym' && (
            <FormTextarea
              label={L.fullGymName}
              name="fullGymName"
              value={data.fullGymName}
              onChange={handleText}
              containerClassName={styles.fieldGroupFull}
              error={errors.fullGymName}
              readOnly={readOnly}
              required
            />
          )}

          {data.trainingLocation === 'home_condo_gym' && (
            <FormTextarea
              label={L.availableEquipment}
              name="availableEquipment"
              value={data.availableEquipment}
              onChange={handleText}
              containerClassName={styles.fieldGroupFull}
              error={errors.availableEquipment}
              readOnly={readOnly}
              required
            />
          )}
        </div>
      </div>

      <div style={{ marginTop: 'var(--spacing-xl)' }}>
        <SectionTitle as="h2" text="Lesões e Dores" spaced />
        <div className={styles.formGridFull}>
          <div className={styles.fieldGroup}>
            <RequiredBooleanLabel>{L.hadInjury}</RequiredBooleanLabel>
            <BooleanRadio name="hadInjury" value={data.hadInjury} onChange={(value) => handleBool('hadInjury', value)} disabled={readOnly} error={errors.hadInjury} />
          </div>

          {data.hadInjury === true && (
            <FormTextarea
              label={L.injuryDescription}
              name="injuryDescription"
              value={data.injuryDescription}
              onChange={handleText}
              error={errors.injuryDescription}
              readOnly={readOnly}
              required
              minRows={readOnly ? 3 : 2}
            />
          )}

          <div className={styles.fieldGroup}>
            <RequiredBooleanLabel>{L.hasPain}</RequiredBooleanLabel>
            <BooleanRadio name="hasPain" value={data.hasPain} onChange={(value) => handleBool('hasPain', value)} disabled={readOnly} error={errors.hasPain} />
          </div>

          {data.hasPain === true && (
            <>
              <FormTextarea
                label={L.painLocation}
                name="painLocation"
                value={data.painLocation}
                onChange={handleText}
                error={errors.painLocation}
                readOnly={readOnly}
                required
                minRows={readOnly ? 3 : 2}
              />

              <div className={styles.fieldGroup}>
                <RequiredBooleanLabel>{L.painLimitsTraining}</RequiredBooleanLabel>
                <BooleanRadio name="painLimitsTraining" value={data.painLimitsTraining} onChange={(value) => handleBool('painLimitsTraining', value)} disabled={readOnly} error={errors.painLimitsTraining} />
              </div>

              {data.painLimitsTraining === true && (
                <FormTextarea
                  label={L.limitedExercises}
                  name="limitedExercises"
                  value={data.limitedExercises}
                  onChange={handleText}
                  error={errors.limitedExercises}
                  readOnly={readOnly}
                  required
                  minRows={readOnly ? 3 : 2}
                />
              )}
            </>
          )}
        </div>
      </div>

      <div style={{ marginTop: 'var(--spacing-xl)' }}>
        <SectionTitle as="h2" text="Histórico de Exercícios" spaced />
        <div className={styles.formGrid}>
          <div className={styles.fieldGroup}>
            <RequiredBooleanLabel>{L.currentlyExercises}</RequiredBooleanLabel>
            <BooleanRadio name="currentlyExercises" value={data.currentlyExercises} onChange={(value) => handleBool('currentlyExercises', value)} disabled={readOnly} error={errors.currentlyExercises} />
          </div>

          {data.currentlyExercises === true && (
            <FormTextarea
              label={L.exerciseModalities}
              name="exerciseModalities"
              value={data.exerciseModalities}
              onChange={handleText}
              containerClassName={styles.fieldGroupFull}
              error={errors.exerciseModalities}
              readOnly={readOnly}
              required
            />
          )}

          {data.currentlyExercises === false && (
            <FormInput
              label={L.timeWithoutExercise}
              name="timeWithoutExercise"
              value={data.timeWithoutExercise}
              onChange={handleText}
              error={errors.timeWithoutExercise}
              readOnly={readOnly}
              required
            />
          )}

          <FormTextarea
            label={L.exerciseHistory}
            name="exerciseHistory"
            value={data.exerciseHistory}
            onChange={handleText}
            containerClassName={styles.fieldGroupFull}
            error={errors.exerciseHistory}
            readOnly={readOnly}
            required
          />

          <div className={styles.fieldGroup}>
            <RequiredBooleanLabel>{L.enjoysTraining}</RequiredBooleanLabel>
            <BooleanRadio name="enjoysTraining" value={data.enjoysTraining} onChange={(value) => handleBool('enjoysTraining', value)} disabled={readOnly} error={errors.enjoysTraining} />
          </div>

          <div className={styles.fieldGroup}>
            <RequiredBooleanLabel>{L.usedToEffort}</RequiredBooleanLabel>
            <BooleanRadio name="usedToEffort" value={data.usedToEffort} onChange={(value) => handleBool('usedToEffort', value)} disabled={readOnly} error={errors.usedToEffort} />
          </div>
        </div>
      </div>

      <div style={{ marginTop: 'var(--spacing-xl)' }}>
        <SectionTitle as="h2" text="Saúde" spaced />
        <div className={styles.formGrid}>
          <div className={styles.fieldGroup}>
            <RequiredBooleanLabel>{L.hasNutritionalSupport}</RequiredBooleanLabel>
            <BooleanRadio name="hasNutritionalSupport" value={data.hasNutritionalSupport} onChange={(value) => handleBool('hasNutritionalSupport', value)} disabled={readOnly} error={errors.hasNutritionalSupport} />
          </div>

          <FormTextarea
            label={L.dietDetail}
            name="dietDetail"
            value={data.dietDetail}
            onChange={handleText}
            containerClassName={styles.fieldGroupFull}
            error={errors.dietDetail}
            readOnly={readOnly}
            required
          />

          <div className={styles.fieldGroup}>
            <RequiredBooleanLabel>{L.takesMedication}</RequiredBooleanLabel>
            <BooleanRadio name="takesMedication" value={data.takesMedication} onChange={(value) => handleBool('takesMedication', value)} disabled={readOnly} error={errors.takesMedication} />
          </div>

          {data.takesMedication === true && (
            <FormTextarea
              label={L.medications}
              name="medications"
              value={data.medications}
              onChange={handleText}
              containerClassName={styles.fieldGroupFull}
              error={errors.medications}
              readOnly={readOnly}
              required
            />
          )}

          <div className={styles.fieldGroup}>
            <RequiredBooleanLabel>{L.hadSurgery}</RequiredBooleanLabel>
            <BooleanRadio name="hadSurgery" value={data.hadSurgery} onChange={(value) => handleBool('hadSurgery', value)} disabled={readOnly} error={errors.hadSurgery} />
          </div>

          {data.hadSurgery === true && (
            <FormTextarea
              label={L.surgeries}
              name="surgeries"
              value={data.surgeries}
              onChange={handleText}
              containerClassName={styles.fieldGroupFull}
              error={errors.surgeries}
              readOnly={readOnly}
              required
            />
          )}

          <div className={styles.fieldGroup}>
            <RequiredBooleanLabel>{L.hasChronicDisease}</RequiredBooleanLabel>
            <BooleanRadio name="hasChronicDisease" value={data.hasChronicDisease} onChange={(value) => handleBool('hasChronicDisease', value)} disabled={readOnly} error={errors.hasChronicDisease} />
          </div>

          {data.hasChronicDisease === true && (
            <FormTextarea
              label={L.chronicDiseases}
              name="chronicDiseases"
              value={data.chronicDiseases}
              onChange={handleText}
              containerClassName={styles.fieldGroupFull}
              error={errors.chronicDiseases}
              readOnly={readOnly}
              required
            />
          )}

          <div className={styles.fieldGroup}>
            <RequiredBooleanLabel>{L.hasFamilyHistory}</RequiredBooleanLabel>
            <BooleanRadio name="hasFamilyHistory" value={data.hasFamilyHistory} onChange={(value) => handleBool('hasFamilyHistory', value)} disabled={readOnly} error={errors.hasFamilyHistory} />
          </div>

          {data.hasFamilyHistory === true && (
            <FormTextarea
              label={L.familyHistoryDescription}
              name="familyHistoryDescription"
              value={data.familyHistoryDescription}
              onChange={handleText}
              containerClassName={styles.fieldGroupFull}
              error={errors.familyHistoryDescription}
              readOnly={readOnly}
              required
            />
          )}
        </div>
      </div>

      <div style={{ marginTop: 'var(--spacing-xl)' }}>
        <SectionTitle as="h2" text="Maternidade" spaced />
        <div className={styles.formGrid}>
          <div className={styles.fieldGroup}>
            <RequiredBooleanLabel>{L.hasChildren}</RequiredBooleanLabel>
            <BooleanRadio name="hasChildren" value={data.hasChildren} onChange={(value) => handleBool('hasChildren', value)} disabled={readOnly} error={errors.hasChildren} />
          </div>

          {data.hasChildren === true && (
            <>
              <FormTextarea
                label={L.timeSinceDelivery}
                name="timeSinceDelivery"
                value={data.timeSinceDelivery}
                onChange={handleText}
                containerClassName={styles.fieldGroupFull}
                error={errors.timeSinceDelivery}
                readOnly={readOnly}
                required
              />

              <div className={styles.fieldGroup}>
                <RequiredBooleanLabel>{L.abdominalDiastasis}</RequiredBooleanLabel>
                <BooleanRadio name="abdominalDiastasis" value={data.abdominalDiastasis} onChange={(value) => handleBool('abdominalDiastasis', value)} disabled={readOnly} error={errors.abdominalDiastasis} />
              </div>

              {data.abdominalDiastasis === true && (
                <FormTextarea
                  label="Tratou a diástase?"
                  name="diastasisTreatment"
                  value={data.diastasisTreatment}
                  onChange={handleText}
                  containerClassName={styles.fieldGroupFull}
                  error={errors.diastasisTreatment}
                  readOnly={readOnly}
                  required
                />
              )}
            </>
          )}
        </div>
      </div>

      <div style={{ marginTop: 'var(--spacing-xl)' }}>
        <SectionTitle as="h2" text="Objetivos e Motivação" spaced />
        <div className={styles.formGrid}>
          <div className={`${styles.goalDetailsRow} ${styles.alignedFieldRow}`}>
            <FormSelect
              label={L.mainGoal}
              name="mainGoal"
              value={data.mainGoal}
              onChange={handleText}
              options={[...PUBLIC_MAIN_GOAL_OPTIONS]}
              containerClassName={styles.alignedField}
              error={errors.mainGoal}
              readOnly={readOnly}
              required
            />

            <FormTextarea
              label={L.goalDetails}
              name="goalDetails"
              value={data.goalDetails}
              onChange={handleText}
              containerClassName={styles.alignedField}
              error={errors.goalDetails}
              readOnly={readOnly}
              required
            />

            <FormSelect
              label={L.referralSource}
              name="referralSource"
              value={data.referralSource}
              onChange={handleText}
              options={[...PUBLIC_REFERRAL_SOURCE_OPTIONS]}
              containerClassName={styles.alignedField}
              error={errors.referralSource}
              readOnly={readOnly}
              required
            />
          </div>

          <div className={`${styles.formGrid2Col} ${styles.alignedFieldRow}`}>
            <FormTextarea
              label={L.additionalInfo}
              name="additionalInfo"
              value={data.additionalInfo}
              onChange={handleText}
              containerClassName={styles.alignedField}
              error={errors.additionalInfo}
              readOnly={readOnly}
              required
            />

            <FormTextarea
              label={L.motivation}
              name="motivation"
              value={data.motivation}
              onChange={handleText}
              containerClassName={styles.alignedField}
              error={errors.motivation}
              readOnly={readOnly}
              required
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnamneseStep;
